import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Kpi, BucketBadge, StatusBadge, Empty, AiBadge } from "@/components/ui-bits";
import { fmtINR, fmtNum, fmtCr } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { RowQuickActions, CommTemplateDialog } from "@/components/QuickActions";
import { ActivityLoggerButton } from "@/components/ActivityLogger";
import { toast } from "sonner";
import { ArrowLeft, MagnifyingGlass, Eye, FileArrowDown, UploadSimple } from "@phosphor-icons/react";

const ACCOUNT_TEMPLATE_CSV = "customer_id,loan_id,borrower_name,mobile,alternate_mobile,email,city,state,pincode,loan_amount,disbursal_date,product,loan_tenure,emi_amount,outstanding_amount,principal_outstanding,interest_outstanding,dpd,emi_due_date,last_payment_date,last_payment_amount,total_overdue_amount,assigned_agency,collection_manager,recovery_status,recovery_probability,account_classification\nCUST50001,L5001,Ramesh Kumar,+919812345678,+919812300000,r@mail.com,Mumbai,Maharashtra,400001,200000,2025-01-15,Personal Loan,24 months,9000,150000,123000,27000,45,2026-07-05,2026-05-20,9000,60000,Apex Recovery Partners,Priya Nair,partial,Medium,Settlement Candidate";
const TEMPLATE_HREF = "data:text/csv;charset=utf-8," + encodeURIComponent(ACCOUNT_TEMPLATE_CSV);

const BUCKETS = ["Current", "1-30", "31-60", "61-90", "90+"];
const CLASS_COLOR = {
  "Likely To Pay": "bg-emerald-100 text-emerald-800",
  "Settlement Candidate": "bg-blue-100 text-blue-800",
  "Skip Trace Required": "bg-amber-100 text-amber-800",
  "Legal Candidate": "bg-red-100 text-red-800",
};

export default function TenantAccounts() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [bucket, setBucket] = useState("");
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [commCtx, setCommCtx] = useState(null);

  useEffect(() => {
    api.get(`/tenants/${tenantId}`).then((r) => setTenant(r.data)).catch(() => {});
    api.get(`/dashboard/stats`, { params: { tenant_id: tenantId } }).then((r) => setStats(r.data)).catch(() => {});
    api.get(`/templates`, { params: { tenant_id: tenantId } }).then((r) => setTemplates(r.data)).catch(() => {});
  }, [tenantId]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { tenant_id: tenantId, limit };
    if (q) params.q = q;
    if (bucket) params.bucket = bucket;
    if (status) params.status = status;
    const r = await api.get("/accounts", { params });
    setData(r.data);
    setLoading(false);
  }, [tenantId, q, bucket, status, limit]);

  useEffect(() => { setLimit(50); }, [q, bucket, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="tenant-accounts-page">
      <button onClick={() => navigate("/tenants")} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
        <ArrowLeft size={16} /> Back to tenants
      </button>

      <PageHeader
        title={tenant ? tenant.company_name : "Tenant Accounts"}
        subtitle={`${fmtNum(data.total)} accounts in portfolio • Super Admin can upload & manage`}
      >
        <a href={TEMPLATE_HREF} download="recoveryos_accounts_template.csv">
          <Button variant="outline" className="rounded-sm gap-2" data-testid="tenant-download-template">
            <FileArrowDown size={16} weight="bold" /> Download Template
          </Button>
        </a>
        <TenantUploadDialog tenantId={tenantId} onDone={load} />
        <span className="inline-flex items-center gap-1.5 text-xs bg-secondary px-2.5 py-1 rounded-sm font-semibold">
          <Eye size={14} weight="bold" /> Super Admin
        </span>
      </PageHeader>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Kpi label="Total Accounts" value={fmtNum(stats.total_accounts)} />
          <Kpi label="Total Outstanding" value={fmtCr(stats.total_outstanding)} accent="text-destructive" />
          <Kpi label="Recovered" value={fmtCr(stats.recovered)} sub={`${stats.recovery_rate}% recovery rate`} accent="text-emerald-600" />
          <Kpi label="Accounts 90+ DPD" value={fmtNum(stats.accounts_above_90)} accent="text-destructive" />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="tenant-account-search" placeholder="Search by name, loan ID or mobile…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-sm" />
        </div>
        <Select value={bucket || "all"} onValueChange={(v) => setBucket(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40 rounded-sm"><SelectValue placeholder="DPD Bucket" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Buckets</SelectItem>
            {BUCKETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-36 rounded-sm"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {["active", "ptp", "closed"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="label-caps border-b border-border bg-secondary/40">
              <th className="text-left p-3">Loan ID</th>
              <th className="text-left p-3">Borrower</th>
              <th className="text-right p-3">Outstanding</th>
              <th className="text-center p-3">DPD</th>
              <th className="text-center p-3">Bucket</th>
              <th className="text-center p-3">AI</th>
              <th className="text-left p-3">Class</th>
              <th className="text-left p-3">Agent</th>
              <th className="text-center p-3">Status</th>
              <th className="text-center p-3">Quick Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((a) => (
              <tr key={a.account_id} onClick={() => navigate(`/accounts/${a.account_id}`)} className="border-b border-border last:border-0 hover:bg-secondary/60 cursor-pointer" data-testid={`tenant-account-row-${a.loan_id}`}>
                <td className="p-3 mono font-medium text-primary">{a.loan_id}</td>
                <td className="p-3">
                  <div className="font-medium">{a.borrower_name}</div>
                  <div className="text-xs text-muted-foreground mono">{a.mobile}</div>
                </td>
                <td className="p-3 text-right mono font-semibold">{fmtINR(a.outstanding_amount)}</td>
                <td className="p-3 text-center mono">{a.dpd}</td>
                <td className="p-3 text-center"><BucketBadge bucket={a.bucket} /></td>
                <td className="p-3 text-center"><AiBadge score={a.ai_score} band={a.ai_band} /></td>
                <td className="p-3">
                  {a.account_classification
                    ? <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-sm ${CLASS_COLOR[a.account_classification] || "bg-secondary"}`}>{a.account_classification}</span>
                    : <span className="text-xs text-muted-foreground">—</span>}
                </td>
                <td className="p-3 text-muted-foreground">{a.assigned_agent_name || <span className="italic">Unassigned</span>}</td>
                <td className="p-3 text-center"><StatusBadge status={a.status} /></td>
                <td className="p-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-center gap-0.5">
                    <RowQuickActions account={a} onPick={(acc, ch) => setCommCtx({ account: acc, channel: ch })} onLogged={load} />
                    <ActivityLoggerButton accountId={a.account_id} onSaved={load} variant="icon" testIdSuffix={a.loan_id} borrowerName={a.borrower_name} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && data.items.length === 0 && <Empty text="No accounts match your filters" />}
      </div>

      {data.items.length < data.total && (
        <div className="flex items-center justify-center mt-4">
          <Button variant="outline" className="rounded-sm" onClick={() => setLimit((l) => l + 50)} disabled={loading} data-testid="tenant-load-more">
            {loading ? "Loading…" : `Load more (${data.items.length} of ${fmtNum(data.total)})`}
          </Button>
        </div>
      )}

      <CommTemplateDialog ctx={commCtx} templates={templates} onClose={() => setCommCtx(null)} onSent={load} />
    </div>
  );
}

function TenantUploadDialog({ tenantId, onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return toast.error("Choose a CSV file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/accounts/bulk?tenant_id=${tenantId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`${r.data.inserted} accounts uploaded`);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); setResult(null); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-sm gap-2" data-testid="tenant-bulk-upload-button"><UploadSimple size={16} weight="bold" /> Bulk Upload</Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm" data-testid="tenant-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload Accounts</DialogTitle>
          <DialogDescription>Import borrower accounts into this tenant from a CSV file.</DialogDescription>
        </DialogHeader>
        <div className="border-2 border-dashed border-border p-8 text-center">
          <UploadSimple size={32} className="mx-auto text-muted-foreground" />
          <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} className="mt-4 text-sm mx-auto block" data-testid="tenant-csv-input" />
          {file && <p className="text-xs text-muted-foreground mt-2 mono">{file.name}</p>}
        </div>
        <a href={TEMPLATE_HREF} download="recoveryos_accounts_template.csv" className="text-xs text-primary inline-flex items-center gap-1">
          <FileArrowDown size={14} /> Download Template
        </a>
        {result && (
          <div className="text-sm bg-secondary p-3 rounded-sm" data-testid="tenant-upload-result">
            Imported <b>{result.inserted}</b> accounts{result.error_count ? `, ${result.error_count} skipped` : ""}.
          </div>
        )}
        <Button onClick={submit} disabled={busy || !file} className="rounded-sm" data-testid="tenant-upload-submit">{busy ? "Uploading…" : "Upload"}</Button>
      </DialogContent>
    </Dialog>
  );
}
